// client/src/pages/School_Admin/Facilities/LibraryDashboardPage.js
import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, Tabs, Tab, Grid,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Alert, Autocomplete, Card, CardContent, Chip, Paper, Avatar,
  Snackbar, IconButton, Tooltip, InputAdornment, LinearProgress,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
  LibraryBooks as LibraryBooksIcon, AssignmentTurnedIn as AssignmentTurnedInIcon,
  PublishedWithChanges as PublishedWithChangesIcon, WarningAmber as WarningAmberIcon,
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon,
  MenuBook as MenuBookIcon
} from '@mui/icons-material';
import { fetchJson } from '../../../utils/fetchJson';

const BOOK_CATEGORIES = [
  'Fiction', 'Non-Fiction', 'Science', 'Mathematics', 'History',
  'Geography', 'Literature', 'Reference', 'Textbook', 'Biography', 'Other'
];

const emptyBookForm = { title: '', author: '', category: '', isbn: '', publishedYear: '', copies: '' };

const LibraryDashboardPage = () => {
  const [stats, setStats] = useState({ totalBooks: 0, totalCopies: 0, issuedBooks: 0, returnedBooks: 0, overdueBooks: 0 });
  const [tab, setTab] = useState(0);
  // Catalog
  const [books, setBooks] = useState([]);
  const [catLoading, setCatLoading] = useState(false);
  const [bookDialog, setBookDialog] = useState(false);
  const [editingBookId, setEditingBookId] = useState(null);
  const [bookForm, setBookForm] = useState({ ...emptyBookForm });
  const [bookSearch, setBookSearch] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  // Circulation
  const [loans, setLoans] = useState([]);
  const [circLoading, setCircLoading] = useState(false);
  const [loanFilter, setLoanFilter] = useState('');
  const [issueForm, setIssueForm] = useState({ studentId: '', bookId: '', dueDate: '' });
  // Autocomplete pickers
  const [patronInput, setPatronInput] = useState('');
  const [patronOptions, setPatronOptions] = useState([]);
  const [bookInput, setBookInput] = useState('');
  const [bookOptions, setBookOptions] = useState([]);
  // Feedback
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const toast = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // ─── Dashboard Stats ───
  const loadDashboard = useCallback(async () => {
    try {
      const data = await fetchJson('/facilities/library/dashboard', { method: 'GET' });
      const s = data?.stats || {};
      setStats({
        totalBooks: Number(s.totalBooks) || 0,
        totalCopies: Number(s.totalCopies) || 0,
        issuedBooks: Number(s.issuedBooks) || 0,
        returnedBooks: Number(s.returnedBooks) || 0,
        overdueBooks: Number(s.overdueBooks) || 0,
      });
    } catch (_) { /* leave defaults */ }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  useEffect(() => {
    const refresh = () => { loadDashboard(); if (tab === 0) loadCatalog(); if (tab === 1) loadLoans(); };
    window.addEventListener('app:ay:changed', refresh);
    window.addEventListener('app:phase:changed', refresh);
    return () => { window.removeEventListener('app:ay:changed', refresh); window.removeEventListener('app:phase:changed', refresh); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadDashboard, tab]);

  useEffect(() => {
    if (tab === 0) loadCatalog();
    if (tab === 1) loadLoans();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // ─── Catalog ───
  async function loadCatalog() {
    setCatLoading(true);
    try {
      const res = await fetchJson('/facilities/library/catalog', { method: 'GET' });
      setBooks(Array.isArray(res) ? res : (res.books || []));
    } catch (e) {
      toast(e?.message || 'Failed to load catalog', 'error');
      setBooks([]);
    } finally { setCatLoading(false); }
  }

  const openAddBook = () => { setEditingBookId(null); setBookForm({ ...emptyBookForm }); setBookDialog(true); };
  const openEditBook = (b) => {
    setEditingBookId(b.id);
    setBookForm({
      title: b.title || '', author: b.author || '', category: b.category || '',
      isbn: b.isbn || '', publishedYear: b.publishedYear || '', copies: b.copies ?? ''
    });
    setBookDialog(true);
  };

  async function saveBook() {
    if (!bookForm.title || !bookForm.author) { toast('Title and author are required', 'warning'); return; }
    try {
      const payload = {
        title: bookForm.title.trim(), author: bookForm.author.trim(),
        category: bookForm.category || null, isbn: bookForm.isbn || null,
        publishedYear: bookForm.publishedYear ? Number(bookForm.publishedYear) : null,
        copies: bookForm.copies ? Number(bookForm.copies) : 1
      };
      if (editingBookId) {
        await fetchJson(`/facilities/library/catalog/${editingBookId}`, { method: 'PUT', body: payload });
        toast('Book updated');
      } else {
        await fetchJson('/facilities/library/catalog', { method: 'POST', body: payload });
        toast('Book added');
      }
      setBookDialog(false);
      loadCatalog();
      loadDashboard();
    } catch (e) { toast(e?.message || 'Failed to save book', 'error'); }
  }

  async function deleteBook() {
    if (!deleteConfirm) return;
    try {
      await fetchJson(`/facilities/library/catalog/${deleteConfirm.id}`, { method: 'DELETE' });
      toast('Book deleted');
      setDeleteConfirm(null);
      loadCatalog();
      loadDashboard();
    } catch (e) { toast(e?.message || 'Failed to delete book', 'error'); }
  }

  // ─── Circulation ───
  async function loadLoans() {
    setCircLoading(true);
    try {
      const params = loanFilter ? `?status=${loanFilter}` : '';
      const res = await fetchJson(`/facilities/library/loans${params}`, { method: 'GET' });
      setLoans(Array.isArray(res) ? res : (res.loans || []));
    } catch (e) {
      toast(e?.message || 'Failed to load loans', 'error');
      setLoans([]);
    } finally { setCircLoading(false); }
  }

  // Reload when filter changes
  useEffect(() => { if (tab === 1) loadLoans(); }, [loanFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  async function issueBook() {
    if (!issueForm.studentId || !issueForm.bookId || !issueForm.dueDate) { toast('Student, book, and due date are required', 'warning'); return; }
    try {
      await fetchJson('/facilities/library/loans', {
        method: 'POST',
        body: { studentId: issueForm.studentId, bookId: issueForm.bookId, dueDate: issueForm.dueDate }
      });
      toast('Book issued successfully');
      setIssueForm({ studentId: '', bookId: '', dueDate: '' });
      setPatronInput(''); setBookInput('');
      loadLoans();
      loadDashboard();
    } catch (e) { toast(e?.message || 'Failed to issue book', 'error'); }
  }

  async function returnLoan(id) {
    try {
      const res = await fetchJson(`/facilities/library/loans/${id}/return`, { method: 'POST' });
      const fine = res?.loan?.fine;
      toast(fine > 0 ? `Book returned. Late fine: ${fine} FCFA` : 'Book returned successfully');
      loadLoans();
      loadDashboard();
    } catch (e) { toast(e?.message || 'Failed to process return', 'error'); }
  }

  // ─── Search helpers ───
  async function searchPatrons(q) {
    if (!q || q.length < 2) { setPatronOptions([]); return; }
    try {
      const res = await fetchJson(`/facilities/library/search/patrons?q=${encodeURIComponent(q)}`, { method: 'GET' });
      const list = res.patrons || [];
      setPatronOptions(list.map(p => ({ id: p.id, label: p.name, photoUrl: p.photoUrl, className: p.className, studentNumber: p.studentNumber })));
    } catch (_) { setPatronOptions([]); }
  }

  async function searchBooksForIssue(q) {
    if (!q || q.length < 2) { setBookOptions([]); return; }
    try {
      const res = await fetchJson(`/facilities/library/search/books?q=${encodeURIComponent(q)}`, { method: 'GET' });
      const list = res.books || [];
      setBookOptions(list.map(b => ({ id: b.id, label: `${b.title} — ${b.author || ''}`, available: b.available })));
    } catch (_) { setBookOptions([]); }
  }

  // Client-side catalog filter
  const filteredBooks = books.filter(b => {
    if (!bookSearch) return true;
    const q = bookSearch.toLowerCase();
    return (b.title || '').toLowerCase().includes(q) || (b.author || '').toLowerCase().includes(q) || (b.isbn || '').toLowerCase().includes(q);
  });

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box display="flex" alignItems="center" gap={1} mb={3}>
        <MenuBookIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" fontWeight="bold">Library Dashboard</Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <Card sx={{ bgcolor: 'primary.light', color: '#0d1b2a' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="caption">Total Books</Typography>
                  <Typography variant="h3" fontWeight={700}>{stats.totalBooks}</Typography>
                  <Typography variant="caption" color="text.secondary">{stats.totalCopies} total copies</Typography>
                </Box>
                <LibraryBooksIcon sx={{ fontSize: 48, opacity: 0.25 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ bgcolor: 'success.light', color: '#0f5132' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="caption">Currently Issued</Typography>
                  <Typography variant="h3" fontWeight={700}>{stats.issuedBooks}</Typography>
                </Box>
                <AssignmentTurnedInIcon sx={{ fontSize: 48, opacity: 0.25 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ bgcolor: 'info.light', color: '#084298' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="caption">Returned</Typography>
                  <Typography variant="h3" fontWeight={700}>{stats.returnedBooks}</Typography>
                </Box>
                <PublishedWithChangesIcon sx={{ fontSize: 48, opacity: 0.25 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ bgcolor: 'error.light', color: '#58151c' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="caption">Overdue</Typography>
                  <Typography variant="h3" fontWeight={700}>{stats.overdueBooks}</Typography>
                </Box>
                <WarningAmberIcon sx={{ fontSize: 48, opacity: 0.25 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Catalog Management" />
        <Tab label="Book Circulation" />
      </Tabs>

      {/* ═══ CATALOG TAB ═══ */}
      {tab === 0 && (
        <Box>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth size="small" placeholder="Search by title, author, or ISBN..."
                  value={bookSearch} onChange={(e) => setBookSearch(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                />
              </Grid>
              <Grid item xs={12} md={6} display="flex" justifyContent="flex-end">
                <Button variant="contained" startIcon={<AddIcon />} onClick={openAddBook}>Add Book</Button>
              </Grid>
            </Grid>
          </Paper>

          {catLoading && <LinearProgress sx={{ mb: 1 }} />}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell><b>Title</b></TableCell>
                  <TableCell><b>Author</b></TableCell>
                  <TableCell><b>Category</b></TableCell>
                  <TableCell><b>ISBN</b></TableCell>
                  <TableCell align="center"><b>Copies</b></TableCell>
                  <TableCell align="center"><b>Available</b></TableCell>
                  <TableCell align="center"><b>Actions</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredBooks.length === 0 ? (
                  <TableRow><TableCell colSpan={7} align="center"><Typography color="text.secondary" py={3}>No books found</Typography></TableCell></TableRow>
                ) : filteredBooks.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{b.title}</Typography>
                      {b.publishedYear && <Typography variant="caption" color="text.secondary">({b.publishedYear})</Typography>}
                    </TableCell>
                    <TableCell>{b.author}</TableCell>
                    <TableCell>{b.category || '-'}</TableCell>
                    <TableCell><Typography variant="caption">{b.isbn || '-'}</Typography></TableCell>
                    <TableCell align="center">{b.copies ?? '-'}</TableCell>
                    <TableCell align="center">
                      <Chip label={b.available ?? '-'} size="small" color={Number(b.available) > 0 ? 'success' : 'error'} />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => openEditBook(b)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleteConfirm(b)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Book Dialog */}
          <Dialog open={bookDialog} onClose={() => setBookDialog(false)} fullWidth maxWidth="sm">
            <DialogTitle>{editingBookId ? 'Edit Book' : 'Add Book'}</DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={12}><TextField label="Title" value={bookForm.title} onChange={(e) => setBookForm(f => ({ ...f, title: e.target.value }))} fullWidth required /></Grid>
                <Grid item xs={12} sm={6}><TextField label="Author" value={bookForm.author} onChange={(e) => setBookForm(f => ({ ...f, author: e.target.value }))} fullWidth required /></Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select label="Category" value={bookForm.category} onChange={(e) => setBookForm(f => ({ ...f, category: e.target.value }))}>
                      <MenuItem value=""><em>None</em></MenuItem>
                      {BOOK_CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}><TextField label="ISBN" value={bookForm.isbn} onChange={(e) => setBookForm(f => ({ ...f, isbn: e.target.value }))} fullWidth /></Grid>
                <Grid item xs={6} sm={4}><TextField label="Published Year" type="number" value={bookForm.publishedYear} onChange={(e) => setBookForm(f => ({ ...f, publishedYear: e.target.value }))} fullWidth /></Grid>
                <Grid item xs={6} sm={4}><TextField label="Copies" type="number" value={bookForm.copies} onChange={(e) => setBookForm(f => ({ ...f, copies: e.target.value }))} fullWidth /></Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setBookDialog(false)} variant="outlined">Cancel</Button>
              <Button variant="contained" onClick={saveBook}>{editingBookId ? 'Update' : 'Save'}</Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirm */}
          <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent><Typography>Are you sure you want to delete <b>{deleteConfirm?.title}</b>?</Typography></DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteConfirm(null)} variant="outlined">Cancel</Button>
              <Button onClick={deleteBook} variant="contained" color="error">Delete</Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {/* ═══ CIRCULATION TAB ═══ */}
      {tab === 1 && (
        <Box>
          {/* Issue Form */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={1.5}>Issue a Book</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={patronOptions}
                  getOptionLabel={(o) => o?.label || ''}
                  isOptionEqualToValue={(o, v) => o?.id === v?.id}
                  onChange={(e, val) => setIssueForm(f => ({ ...f, studentId: val?.id || '' }))}
                  inputValue={patronInput}
                  onInputChange={(e, v) => { setPatronInput(v); searchPatrons(v); }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar src={option.photoUrl || ''} sx={{ width: 28, height: 28 }}>{(option.label || '').charAt(0)}</Avatar>
                        <Box>
                          <Typography variant="body2">{option.label}</Typography>
                          <Typography variant="caption" color="text.secondary">{option.className}{option.studentNumber ? ` — ${option.studentNumber}` : ''}</Typography>
                        </Box>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => <TextField {...params} label="Student" placeholder="Search by name..." size="small" />}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={bookOptions}
                  getOptionLabel={(o) => o?.label || ''}
                  isOptionEqualToValue={(o, v) => o?.id === v?.id}
                  onChange={(e, val) => setIssueForm(f => ({ ...f, bookId: val?.id || '' }))}
                  inputValue={bookInput}
                  onInputChange={(e, v) => { setBookInput(v); searchBooksForIssue(v); }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box display="flex" justifyContent="space-between" width="100%">
                        <Typography variant="body2">{option.label}</Typography>
                        <Chip label={`${option.available} avail.`} size="small" color={option.available > 0 ? 'success' : 'error'} />
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => <TextField {...params} label="Book" placeholder="Search by title..." size="small" />}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField type="date" label="Due Date" InputLabelProps={{ shrink: true }} size="small"
                  value={issueForm.dueDate} onChange={(e) => setIssueForm(f => ({ ...f, dueDate: e.target.value }))} fullWidth />
              </Grid>
              <Grid item xs={6} md={2}>
                <Button variant="contained" fullWidth onClick={issueBook}>Issue Book</Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Loan Filter */}
          <Box display="flex" gap={1} mb={2}>
            {['', 'issued', 'returned'].map(f => (
              <Chip key={f || 'all'} label={f ? f.charAt(0).toUpperCase() + f.slice(1) : 'All'}
                color={loanFilter === f ? 'primary' : 'default'} variant={loanFilter === f ? 'filled' : 'outlined'}
                onClick={() => setLoanFilter(f)} />
            ))}
          </Box>

          {circLoading && <LinearProgress sx={{ mb: 1 }} />}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell><b>Student</b></TableCell>
                  <TableCell><b>Book</b></TableCell>
                  <TableCell><b>Issued</b></TableCell>
                  <TableCell><b>Due</b></TableCell>
                  <TableCell><b>Returned</b></TableCell>
                  <TableCell align="center"><b>Status</b></TableCell>
                  <TableCell><b>Fine</b></TableCell>
                  <TableCell align="center"><b>Actions</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loans.length === 0 ? (
                  <TableRow><TableCell colSpan={8} align="center"><Typography color="text.secondary" py={3}>No loans found</Typography></TableCell></TableRow>
                ) : loans.map(l => (
                  <TableRow key={l.id} sx={l.isOverdue ? { bgcolor: 'error.50' } : {}}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar src={l.studentPhoto || ''} sx={{ width: 32, height: 32 }}>
                          {(l.studentName || '').charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2">{l.studentName || '-'}</Typography>
                          {l.studentNumber && <Typography variant="caption" color="text.secondary">{l.studentNumber}</Typography>}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{l.bookTitle || '-'}</Typography>
                      {l.bookAuthor && <Typography variant="caption" color="text.secondary">{l.bookAuthor}</Typography>}
                    </TableCell>
                    <TableCell>{l.issuedAt ? new Date(l.issuedAt).toLocaleDateString() : '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color={l.isOverdue ? 'error.main' : 'text.primary'} fontWeight={l.isOverdue ? 600 : 400}>
                        {l.dueAt ? new Date(l.dueAt).toLocaleDateString() : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>{l.returnedAt ? new Date(l.returnedAt).toLocaleDateString() : '-'}</TableCell>
                    <TableCell align="center">
                      <Chip size="small"
                        label={l.isOverdue ? 'Overdue' : l.status === 'returned' ? 'Returned' : 'Issued'}
                        color={l.isOverdue ? 'error' : l.status === 'returned' ? 'success' : 'info'} />
                    </TableCell>
                    <TableCell>{l.fine ? `${l.fine} FCFA` : '-'}</TableCell>
                    <TableCell align="center">
                      {l.status === 'issued' && (
                        <Button size="small" variant="outlined" onClick={() => returnLoan(l.id)}>Return</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default LibraryDashboardPage;
